import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { isLoggedIn, hasPermission, rememberIntendedPath } from './session'
import { SHOPLESS_ROUTES } from './permissions'
import { hasNoShop, mustPickShop, getActiveShopId } from '../shop/activeShop'
import ShopChooser from '../shop/ShopChooser'

/**
 * Nothing authenticated renders without a session (T-018).
 *
 * Before this, every route in the app was reachable by typing its URL. What
 * happened next was not a security hole — the API refuses an unauthenticated
 * request — but it was worse to look at than a login screen: MasterLayout reads
 * getUserDetails()['permissions'] as it renders, that is null with no session,
 * and the whole app went white with a TypeError in the console.
 *
 * The user's destination is remembered, so someone whose session expired
 * halfway through a task comes back to where they were instead of the
 * dashboard.
 *
 * THE ORDER OF THE CHECKS BELOW IS LOAD-BEARING
 *
 *   1. logged in?      nothing else can be answered before this
 *   2. has a shop?     rule 1 — with none, only the catalogue exists
 *   3. picked a shop?  with several and no choice, permissions are the
 *                      intersection, so the answer to check 4 is not yet
 *                      trustworthy
 *   4. permitted?
 *
 * Check 3 sits before check 4 for a specific reason: a multi-shop user who has
 * not chosen holds only what they hold in EVERY shop, so gating on that would
 * hide screens they are perfectly entitled to the moment they pick a shop.
 */
export default function RequireAuth({ children, permission }) {
    const location = useLocation()

    if (!isLoggedIn()) {
        rememberIntendedPath(location.pathname + location.search)
        // replace, so the back button does not bounce them straight back to a
        // screen they still cannot see.
        return <Navigate to="/login" replace />
    }

    // Rule 1: no shop, no shop data. The server refuses everything outside its
    // own allowlist with a 403 (B-9a), so anything else rendered here would be
    // a screen full of failed requests. Sent to the catalogue instead, with the
    // banner in MasterLayout explaining why the rest of the app is missing.
    if (hasNoShop()) {
        return SHOPLESS_ROUTES.includes(location.pathname)
            ? children
            : <Navigate to="/product/list" replace />
    }

    // Several shops and none chosen. Not an error — the honest state before the
    // user has answered. It has to be resolved BEFORE anything is rendered:
    // without a shop the API 422s every write, so a salesperson would find the
    // till refusing each sale with nothing on screen explaining it.
    if (mustPickShop() && !getActiveShopId()) {
        return <ShopChooser />
    }

    if (permission && !hasPermission(permission)) {
        // Deliberately the dashboard rather than a "denied" page. Typing a URL
        // you do not have rights to is nearly always a stale bookmark or a link
        // from a colleague with a different role, and a dead end helps nobody.
        return <Navigate to="/dashboard" replace />
    }

    return children
}
